import { useRef, useState, type FormEvent } from "react";
import { X } from "lucide-react";
import {
  messagingApi,
  type Conversation,
  type Directory,
} from "../../api/messaging.api";
import {
  studentMessagingApi,
  type StudentMessagingContacts,
  type StudentSearchResult,
} from "../../api/student-messaging.api";
import { useAuthStore } from "../../store/auth.store";
import { errorText, useCommand } from "./command";
import type { MessagingTarget } from "./full-page";
import { StudentSearch } from "./StudentSearch";
import { BroadcastToStudents } from "./BroadcastToStudents";
import { BackButton } from "./parts";
import { studentErrorMessage, studentNotice } from "./student-shared";

/** « de Léa », « d’Emma ». */
const of = (name: string) =>
  (/^[aeiouyàâäéèêëîïôöùûüh]/i.test(name) ? "d’" : "de ") + name;

/**
 * Nouveau message : des collègues, des élèves, ou les deux.
 * Seulement des collègues : conversation de l'équipe, comme avant (direct, groupe, salon).
 * Un élève seul : sa boîte commune (secrétariat) ou un échange à deux (moniteur).
 * Un élève avec d'autres élèves ou des collègues : groupe avec élèves.
 */
export function NewMessage({
  directory,
  contacts,
  scope,
  broadcast = false,
  onCreated,
  onCancel,
}: {
  directory: Directory;
  /** Droits du compte dans la messagerie des élèves ; `null` : pas d'accès, rien n'est proposé. */
  contacts: StudentMessagingContacts | null;
  /** Portée du cache de la recherche d'élèves. */
  scope: string;
  /** Ouvert par « Message à tous les élèves » : le message à tous d'abord, « Retour » revient à la liste. */
  broadcast?: boolean;
  onCreated: (target: MessagingTarget) => void;
  onCancel: () => void;
}) {
  const user = useAuthStore((s) => s.user?.id);
  const [type, setType] = useState<Conversation["type"]>("DIRECT"),
    [title, setTitle] = useState(""),
    [members, setMembers] = useState<string[]>([]),
    [students, setStudents] = useState<StudentSearchResult[]>([]),
    [school, setSchool] = useState(directory.schools[0]?.id ?? ""),
    [search, setSearch] = useState(""),
    [busy, setBusy] = useState(false),
    [error, setError] = useState(""),
    [broadcasting, setBroadcasting] = useState(broadcast);
  const command = useCommand(),
    lock = useRef(false);

  const canTeam = directory.role !== "read_only";
  const canOffice = !!contacts?.canOpenOffice,
    canDirect = !!contacts?.canOpenDirect,
    canGroup = !!contacts?.canCreateGroup;
  const canStudent = canOffice || canDirect || canGroup;
  const withStudents = students.length > 0;
  // Un groupe avec élèves ne réunit que l'équipe de l'école (comptes de `/contacts`).
  const staff = contacts?.staff ?? [];
  const inStaff = (id: string) => staff.some((person) => person.id === id);
  const outcome = !withStudents
    ? canTeam
      ? "team"
      : "none"
    : students.length === 1 && !members.length && (canOffice || canDirect)
      ? canOffice
        ? "office"
        : "direct"
      : canGroup
        ? "group"
        : "none";

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (lock.current || outcome === "none") return;
    lock.current = true;
    setBusy(true);
    setError("");
    try {
      if (outcome === "team") {
        const payload = {
          type,
          title: type === "DIRECT" ? "Échange direct" : title,
          participants: members,
          ...(type === "SCHOOL" ? { schoolId: school } : {}),
        };
        const result = await messagingApi.create({
          ...payload,
          requestId: command(payload),
        });
        onCreated({ source: "team", id: result.id });
        return;
      }
      let id: string;
      if (outcome === "office")
        id = (await studentMessagingApi.openOffice(students[0].id)).id;
      else if (outcome === "direct")
        id = (await studentMessagingApi.openDirect(students[0].id)).id;
      else {
        // Même demande rejouée : même `requestId`. Une composition modifiée est une nouvelle demande.
        const payload = {
          title: title.trim(),
          staffIds: members.filter(inStaff),
          candidateIds: students.map((student) => student.id),
        };
        id = (
          await studentMessagingApi.createGroup({
            requestId: command(payload),
            ...payload,
          })
        ).id;
      }
      onCreated({ source: "student", id });
    } catch (err) {
      setError(
        outcome === "team"
          ? errorText(err)
          : studentErrorMessage(err, errorText(err)),
      );
    } finally {
      lock.current = false;
      setBusy(false);
    }
  }

  const matching = (name: string) =>
    name.toLowerCase().includes(search.toLowerCase());
  const available = directory.items.filter(
    (u) =>
      u.id !== user &&
      (withStudents ||
        type !== "SCHOOL" ||
        u.schoolId === school ||
        !u.schoolId) &&
      matching(`${u.firstName} ${u.lastName}`),
  );
  // Équipe de l'école absente de l'annuaire : proposée pour un groupe avec élèves seulement.
  const staffOnly = withStudents
    ? staff.filter(
        (person) =>
          person.id !== user &&
          !directory.items.some((u) => u.id === person.id) &&
          matching(person.name),
      )
    : [];
  const single = !withStudents && type === "DIRECT";
  const toggle = (id: string, checked: boolean) =>
    setMembers(
      checked
        ? single
          ? [id]
          : [...members, id]
        : members.filter((other) => other !== id),
    );
  // Avec un élève, seule l'équipe de son école peut être réunie, et seulement dans un groupe.
  const colleagueLocked = (id: string) =>
    withStudents && (!canGroup || !inStaff(id));

  function addStudent(student: StudentSearchResult) {
    setStudents((known) => [...known, student]);
    setMembers((known) => known.filter(inStaff));
  }
  function removeStudent(id: string) {
    const left = students.filter((student) => student.id !== id);
    setStudents(left);
    if (left.length) return;
    // Retour entre collègues : seulement l'annuaire interne (et l'école du salon), comme avant.
    const kept = members.filter((member) =>
      directory.items.some(
        (u) =>
          u.id === member &&
          (type !== "SCHOOL" || u.schoolId === school || !u.schoolId),
      ),
    );
    setMembers(kept);
    // Un message direct n'a qu'un destinataire.
    if (type === "DIRECT" && kept.length > 1) setType("GROUP");
  }

  const named =
    outcome === "group" || (outcome === "team" && type !== "DIRECT");
  const count = (n: number, one: string, many: string) =>
    `${n} ${n > 1 ? many : one}`;
  const memberName = () => {
    const person = directory.items.find((u) => u.id === members[0]);
    return person ? `${person.firstName} ${person.lastName}` : "ce collègue";
  };
  const staffCount = members.filter(inStaff).length;
  // Ce qui sera créé, dit avant de valider.
  let summary = "";
  if (outcome === "office")
    summary = `Sera ouverte : la boîte commune ${of(students[0].name)}. Tout le secrétariat la lit et y répond.`;
  else if (outcome === "direct")
    summary = `Sera ouvert : un échange à deux avec ${students[0].name}.`;
  else if (outcome === "group")
    summary = `Sera créé : un groupe avec élèves (${count(students.length, "élève", "élèves")}${staffCount ? `, ${count(staffCount, "collègue", "collègues")}` : ""} et vous). Sa composition ne pourra plus être modifiée.`;
  else if (outcome === "team" && members.length)
    summary =
      type === "DIRECT"
        ? `Sera créé : un message direct avec ${memberName()}, entre collègues.`
        : type === "GROUP"
          ? `Sera créé : un groupe privé entre collègues (${count(members.length, "personne", "personnes")} et vous), sans élève.`
          : `Sera créé : un salon ${type === "SCHOOL" ? "d’école" : "d’agence"}, entre collègues, sans élève.`;
  else if (withStudents)
    summary = "Votre compte ne peut pas créer de groupe avec des élèves.";

  // Message à tous les élèves : réservé au secrétariat qui écrit (comme l'ouverture d'une boîte commune).
  if (broadcasting && canOffice)
    return (
      <BroadcastToStudents
        onDone={() => (broadcast ? onCancel() : setBroadcasting(false))}
      />
    );

  return (
    <form
      onSubmit={submit}
      className="messaging-form messaging-new"
      aria-label="Nouveau message"
    >
      <header className="messaging-conv-head">
        <BackButton onBack={onCancel} />
        <div className="messaging-conv-title">
          <h2>Nouveau message</h2>
          {canStudent && (
            <p className="messaging-conv-sub">
              Choisissez des collègues, des élèves, ou les deux.
            </p>
          )}
        </div>
        {canOffice && (
          <button
            type="button"
            className="btn btn--ghost"
            onClick={() => setBroadcasting(true)}
          >
            Écrire à tous les élèves
          </button>
        )}
      </header>
      {withStudents ? (
        <p className="messaging-student-notice">
          {outcome === "group"
            ? `${studentNotice("GROUP")} Les élèves lisent tout ce qui s’y écrit.`
            : `${studentNotice()} L’élève lit tout ce qui s’y écrit.`}
        </p>
      ) : (
        <p className="messaging-hint">
          Les participants voient tout l’historique. Les DM et groupes sont
          réservés à leurs participants.
        </p>
      )}
      <fieldset disabled={busy}>
        {!withStudents && canTeam && (
          <label>
            Type
            <select
              value={type}
              onChange={(e) => {
                setType(e.target.value as Conversation["type"]);
                setMembers([]);
              }}
            >
              <option value="DIRECT">Message direct</option>
              <option value="GROUP">Groupe privé</option>
              {["owner", "manager"].includes(directory.role) && (
                <>
                  <option value="SCHOOL">Salon école</option>
                  {directory.agencyId && (
                    <option value="AGENCY">Salon agence</option>
                  )}
                </>
              )}
            </select>
          </label>
        )}
        {!canTeam && (
          <p className="messaging-hint">
            Votre compte ne peut pas créer de conversation entre collègues.
          </p>
        )}
        {named && (
          <label>
            Nom de la conversation
            <input
              required
              maxLength={120}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </label>
        )}
        {!withStudents && type === "SCHOOL" && (
          <label>
            École
            <select
              value={school}
              onChange={(e) => {
                setSchool(e.target.value);
                setMembers([]);
              }}
            >
              {directory.schools.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </label>
        )}
        <label>
          Rechercher une personne
          <input value={search} onChange={(e) => setSearch(e.target.value)} />
        </label>
        <div className="messaging-members">
          {available.map((u) => (
            <label key={u.id}>
              <input
                type="checkbox"
                disabled={colleagueLocked(u.id)}
                checked={members.includes(u.id) && !colleagueLocked(u.id)}
                onChange={(e) => toggle(u.id, e.target.checked)}
              />
              {u.firstName} {u.lastName}
              {withStudents && !inStaff(u.id) && (
                <>
                  {" "}
                  <small>hors de l’équipe de cette école</small>
                </>
              )}
            </label>
          ))}
          {staffOnly.map((person) => (
            <label key={person.id}>
              <input
                type="checkbox"
                disabled={colleagueLocked(person.id)}
                checked={members.includes(person.id)}
                onChange={(e) => toggle(person.id, e.target.checked)}
              />
              {person.name}
            </label>
          ))}
        </div>
        {directory.hasMore && (
          <p>Annuaire limité aux 500 premières personnes.</p>
        )}
        {contacts && (
          <fieldset className="messaging-students">
            <legend>Élèves</legend>
            {!canStudent ? (
              <p className="messaging-hint">
                Votre compte ne peut pas écrire aux élèves.
              </p>
            ) : (
              <>
                {withStudents && (
                  <ul
                    className="messaging-student-chips"
                    aria-label="Élèves ajoutés"
                  >
                    {students.map((student) => (
                      <li key={student.id}>
                        {student.name}
                        <button
                          type="button"
                          aria-label={`Retirer ${student.name}`}
                          title={`Retirer ${student.name}`}
                          onClick={() => removeStudent(student.id)}
                        >
                          <X size={13} aria-hidden="true" />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
                {(canGroup || (!withStudents && !members.length)) && (
                  <StudentSearch
                    scope={scope}
                    label="Ajouter un élève"
                    exclude={students.map((student) => student.id)}
                    onPick={addStudent}
                  />
                )}
                {!canGroup && (
                  <p className="messaging-hint">
                    Votre compte ne peut pas créer de groupe avec des élèves :
                    écrivez à un seul élève, sans collègue.
                  </p>
                )}
              </>
            )}
          </fieldset>
        )}
        {canStudent && summary && (
          <p role="status" className="messaging-outcome">
            {summary}
          </p>
        )}
        <div className="messaging-form-actions">
          <button className="btn btn--ghost" type="button" onClick={onCancel}>
            Annuler
          </button>
          <button
            className="btn btn--primary"
            disabled={
              busy ||
              outcome === "none" ||
              (outcome === "team" && !members.length) ||
              (outcome === "group" && !title.trim())
            }
          >
            {outcome === "office"
              ? "Ouvrir la boîte commune"
              : outcome === "direct"
                ? "Ouvrir l’échange"
                : outcome === "group"
                  ? "Créer le groupe"
                  : "Créer la conversation"}
          </button>
        </div>
      </fieldset>
      {error && <p role="alert">{error}</p>}
    </form>
  );
}
